<template>
    <!-- TODO: REFACTOR, split chance and state card -->
    <card-normal>
        <template #sections>
            <card-section area="card-header">
                <text-decorator bold :size="FontSize.sm">
                    Status, fase en reden
                </text-decorator>
            </card-section>

            <card-section
                v-if="props.lead.phase || canChangePhase"
                area="card-footer"
                class="state-card-border"
                :color="Color.none"
            >
                <flex-container :gap="Size.sm">
                    <div v-if="props.lead.phase && !canChangePhase">
                        <bar-block :color="phaseColor">
                            <template #title>
                                {{ props.lead.phase.name }}
                            </template>
                            <template v-if="props.lead.status.equals(Status.OPEN)" #subtitle>
                                {{ props.lead.reason?.name }}
                            </template>
                        </bar-block>
                    </div>

                    <template v-else-if="canChangePhase">
                        <input-group
                            v-model="currentPhase"
                            @update:model-value="onEdited('phase')"
                        >
                            <template #input>
                                <select-input
                                    v-model:subitem-value="currentPhaseReason"
                                    subitems-key-name="reasons"
                                    subitems-value-key-name="id"
                                    subitems-label-key-name="name"
                                    :items="filteredAssignablePhases ?? []"
                                    variant="inline"
                                    arrow-variant="unfold"
                                    no-scroll
                                    @update:subitem-value="onEdited('phase')"
                                >
                                    <template #active>
                                        <bar-block :color="phaseColor">
                                            <template #title>
                                                {{ currentPhase?.name ?? 'Kies een fase' }}
                                            </template>
                                            <template #subtitle>
                                                {{ currentPhaseReason?.name }}
                                            </template>
                                        </bar-block>
                                    </template>
                                    <template #item="{ bindings }">
                                        <bar-block :color="bindings.item.color">
                                            <template #title>
                                                {{ bindings.item.name }}
                                            </template>
                                        </bar-block>
                                    </template>
                                </select-input>
                            </template>
                        </input-group>
                        <transition name="fade">
                            <flex-container
                                v-if="stateHasChanges && !statusHasChanges"
                                :gap="Size.sm"
                                justify-content="flex-end"
                            >
                                <text-decorator
                                    v-if="currentPhase?.reasonRequired && !currentPhaseReason"
                                    :color="Color.red500"
                                    :size="FontSize.xs"
                                >
                                    Voor deze fase is een reden verplicht
                                </text-decorator>
                                <template v-else>
                                    <button-outline @click="cancelState">
                                        Annuleren
                                    </button-outline>
                                    <button-block @click="changeState">
                                        Opslaan
                                    </button-block>
                                </template>
                            </flex-container>
                        </transition>
                    </template>
                </flex-container>
            </card-section>
        </template>

        <flex-container :gap="Size.sm">
            <template v-if="props.lead.status.equals(Status.OPPORTUNITY) && isUserOfAssignedCompany">
                <button-block
                    without-loading
                    @completed="takeOpportunityConfirm"
                >
                    Oppakken
                </button-block>
                <button-block
                    :color="Color.red500"
                    without-loading
                    @completed="closeOpportunityConfirm"
                >
                    Sluiten
                </button-block>
            </template>
            <div v-else-if="!isUserOfAssignedCompany || !stateIsEditable">
                <dot-block :color="getStatusColor(props.lead.status)">
                    <template #title>
                        {{ props.lead.status.label }}
                    </template>
                    <template v-if="!props.lead.status.equals(Status.OPEN)" #subtitle>
                        {{ props.lead.reason?.name }}
                    </template>
                </dot-block>
            </div>
            <template v-else>
                <input-group
                    v-model="currentStatus"
                    @update:model-value="onEdited('status')"
                >
                    <template #input>
                        <select-input
                            v-model:subitem-value="currentStatusReason"
                            subitems-key-name="reasons"
                            subitems-value-key-name="id"
                            subitems-label-key-name="name"
                            :items="assignableStatusses ?? []"
                            variant="inline"
                            arrow-variant="unfold"
                            @update:subitem-value="onEdited('status')"
                        >
                            <template #active>
                                <dot-block :color="getStatusColor(currentStatus)">
                                    <template #title>
                                        {{ currentStatus.label }}
                                    </template>
                                    <template v-if="!currentStatus.equals(Status.OPEN)" #subtitle>
                                        {{ currentStatusReason?.name }}
                                    </template>
                                </dot-block>
                            </template>
                            <template #item="{ bindings }">
                                <dot-block :color="getStatusColor(bindings.item)">
                                    <template #title>
                                        {{ bindings.item.label }}
                                    </template>
                                </dot-block>
                            </template>
                        </select-input>
                    </template>
                </input-group>
                <transition name="fade">
                    <flex-container
                        v-if="statusHasChanges"
                        :gap="Size.sm"
                        justify-content="flex-end"
                    >
                        <text-decorator
                            v-if="currentStatus?.reasonRequired && !currentStatusReason"
                            :color="Color.red500"
                            :size="FontSize.xs"
                        >
                            Voor deze status is een reden verplicht
                        </text-decorator>
                        <template v-else>
                            <button-outline @click="cancelStatus">
                                Annuleren
                            </button-outline>
                            <button-block @click="changeStatus">
                                Opslaan
                            </button-block>
                        </template>
                    </flex-container>
                </transition>
            </template>
        </flex-container>
        <confirmation-dialog
            ref="closeOpportunityConfirmationDialog"
            label="Weet je zeker dat je dit wilt doen?"
            description="Met deze actie verlies je de lead en zal de status naar 'verloren' gaan."
        >
            <template #actions="{ bindings }">
                <button-outline @click="bindings.overlay.closeOverlay();">
                    Annuleren
                </button-outline>
                <button-block @loading="closeOpportunity">
                    Bevestig
                </button-block>
            </template>
        </confirmation-dialog>
        <confirmation-dialog
            ref="takeOpportunityConfirmationDialog"
            label="Weet je zeker dat je dit wilt doen?"
            description="Met deze actie open je de lead met de eerste fase van de pijplijn."
        >
            <template #actions="{ bindings }">
                <button-outline @click="bindings.overlay.closeOverlay();">
                    Annuleren
                </button-outline>
                <button-block @loading="takeOpportunity">
                    Bevestig
                </button-block>
            </template>
        </confirmation-dialog>
    </card-normal>
</template>

<script setup lang="ts">
    import {
        BarBlock,
        DotBlock,
        CardNormal,
        CardSection,
        Color,
        TextDecorator,
        FontSize,
        Size,
        ButtonOutline,
        FlexContainer,
        InputGroup,
        SelectInput,
        ButtonBlock,
        type ExposedOverlay,
        NotificationBlock,
    } from '@keyvue/one-ui';

    import { computed, nextTick, ref, type Ref, watch } from 'vue';
    import { sendTaskTiming, useLeadMoveTiming } from '@/utils/taskTiming';
    import { Status, Phase, Lead, Reason, StatusSettings, ReasonId, type PipelineSubResource } from '@keyvue/one-api-client';
    import { createResourceIdentifier, Referral, ReferralAPI, ReferralStatus } from '@keyvue/one-api-client-legacy';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';
    import useClient from '@composables/useClient';
    import useLeadSecurity from '@composables/security/useLeadSecurity';

    export interface Props {
        lead: Lead
    }

    const props = defineProps<Props>();
    const { getReactiveCollection, client, currentLegacyCompany } = useClient();
    const { notify, notifySaving, notifySaved } = useNotify();
    const { isUserOfAssignedCompany } = await useLeadSecurity(props.lead);
    const eventBus = useEventBus();

    const statusHasChanges = ref(false);
    const stateHasChanges = ref(false);
    const currentStatus = ref(props.lead.status) as Ref<Status>;
    const currentStatusReason = ref<Reason | null>(props.lead.reason);
    const currentPhase = ref(props.lead.phase);
    const currentPhaseReason = ref<Reason | null>(props.lead.reason);

    // UX measurement: how long moving a lead takes
    const moveTiming = useLeadMoveTiming();
    // Called from the selects; after nextTick the (pre-flush) watchers below have recomputed the dirty flags, so this
    // knows whether a change is left: clicking the selected item again leaves none
    const onEdited = (part: 'status' | 'phase') => nextTick(() => {
        moveTiming.edited(part, part === 'status' ? statusHasChanges.value : stateHasChanges.value);
    });

    const takeOpportunityConfirmationDialog = ref<ExposedOverlay>();
    const closeOpportunityConfirmationDialog = ref<ExposedOverlay>();

    const [
        reasons,
        statusSettingsCollection,
        assignablePhasesCollection,
    ] = await Promise.all([
        await client.getSubResources<PipelineSubResource, Reason>(props.lead?.pipeline, Reason, 'available-reasons'),
        await getReactiveCollection(StatusSettings, { company: props.lead.pipeline.company.id }),
        await getReactiveCollection(Phase, { pipeline: props.lead.pipeline.id, active: true }),
    ]);

    const stateIsEditable = computed(() => props.lead.status?.equals(Status.OPEN));

    const canChangePhase = computed(() => isUserOfAssignedCompany.value && stateIsEditable.value);

    const assignableStatusses = computed(
        () => Status.values
            .filter((status) => !status.equals(Status.OPPORTUNITY))
            .map((status) => {
                const filterStatusSettings = statusSettingsCollection.data.value.find((statusSettings) => statusSettings.status.equals(status.value));
                const filterStatus = status;
                filterStatus.reasons = Status.OPEN.equals(status) ? [] : reasons.data.filter((reason) => reason.status.equals(status.value));
                if (filterStatusSettings && filterStatusSettings.nullReasonEnabled && !Status.OPEN.equals(status)) {
                    filterStatus.reasons.unshift({
                        id: new ReasonId(''),
                        name: 'Niet van toepassing',
                    } as Reason);
                }
                filterStatus.reasonRequired = filterStatusSettings?.reasonRequired ?? false;

                return filterStatus;
            }),
    );

    const filteredAssignablePhases = computed(
        () => assignablePhasesCollection.data.value
            .map((phase) => {
                const filterPhase = phase;
                filterPhase.reasons = filterPhase.reasons.filter((reason) => reason.active);
                if (phase.nullReasonEnabled) {
                    filterPhase.reasons.unshift({
                        id: new ReasonId(''),
                        name: 'Niet van toepassing',
                    } as Reason);
                }
                return filterPhase;
            })
            .sort((a, b) => a.compareTo(b)),
    );

    const getStatusColor = (status: Status) => {
        if (status.equals(Status.WON)) {
            return Color.green500;
        }

        if (status.equals(Status.LOST)) {
            return Color.red500;
        }

        return Color.gray400;
    };

    const cancelStatus = () => {
        currentStatus.value = props.lead.status;
        currentStatusReason.value = props.lead.reason;
        statusHasChanges.value = false;
        moveTiming.cancelled('status');
    };

    const cancelState = () => {
        currentPhase.value = props.lead.phase;
        currentPhaseReason.value = props.lead.reason;
        stateHasChanges.value = false;
        moveTiming.cancelled('phase');
    };

    const phaseColor = computed(() => currentPhase.value?.color as Color || Color.gray400);

    const changeReferralField = async (callback: (referral: Referral) => Referral) => {
        const closeNotification = notifySaving(undefined, 0);
        const legacyReferral = await ReferralAPI.get(props.lead.id.value);
        await ReferralAPI.update(props.lead.id.value, callback(legacyReferral));
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    const changeStatus = async () => {
        const stoppedMove = moveTiming.stop('status', currentStatus.value?.value);
        await changeReferralField((referral) => {
            const localReferral = referral;

            if (!currentStatus?.value) {
                return localReferral;
            }

            localReferral.status = currentStatus.value.value as ReferralStatus;
            localReferral.newStatusReason = currentStatusReason.value ? createResourceIdentifier('status_reasons', currentStatusReason.value.id.value) : null;

            if (currentStatus.value.equals(Status.LOST)) {
                localReferral.status = ReferralStatus.CLOSED;
            }

            if (currentStatus.value.equals(Status.WON)) {
                localReferral.status = ReferralStatus.SUCCESSFUL;

                if (currentLegacyCompany.value.orderSettings.enabled) {
                    const willCreateOrder = !!currentLegacyCompany.value.orderSettings.pipelineMapping[props.lead?.pipeline?.id.value];

                    if (willCreateOrder) {
                        setTimeout(() => {
                            eventBus.dispatch('resource-manipulated', 'Order');
                            notify({
                                component: NotificationBlock,
                                duration: 3000,
                                props: {
                                    title: 'Opdracht aangemaakt',
                                    text: 'Er is meteen ook een opdracht aangemaakt voor deze lead',
                                },
                            });
                        }, 5000);
                    }
                }
            }

            return localReferral;
        });

        sendTaskTiming(stoppedMove);
        statusHasChanges.value = false;

        eventBus.dispatch('resource-manipulated', 'Lead');
    };

    const changeState = async () => {
        const stoppedMove = moveTiming.stop('phase');
        await changeReferralField((referral) => {
            const localReferral = referral;

            if (!currentPhase?.value) {
                return localReferral;
            }

            localReferral.phase = createResourceIdentifier('pipeline_phases', currentPhase.value.id.value);
            localReferral.newStatusReason = currentPhaseReason.value ? createResourceIdentifier('status_reasons', currentPhaseReason.value.id.value) : null;
            return localReferral;
        });

        sendTaskTiming(stoppedMove);
        stateHasChanges.value = false;

        eventBus.dispatch('resource-manipulated', 'Lead');
    };

    const closeOpportunityConfirm = () => {
        closeOpportunityConfirmationDialog.value?.openOverlay();
    };

    const closeOpportunity = async () => {
        await changeReferralField((referral) => ({
            ...referral,
            status: ReferralStatus.CLOSED,
        }) as Referral);

        eventBus.dispatch('resource-manipulated', 'Lead');

        closeOpportunityConfirmationDialog.value?.closeOverlay();
    };

    const takeOpportunityConfirm = () => {
        takeOpportunityConfirmationDialog.value?.openOverlay();
    };

    const takeOpportunity = async () => {
        const phase = filteredAssignablePhases.value[0];

        await changeReferralField((referral) => ({
            ...referral,
            status: ReferralStatus.OPEN,
            statusReason: null,
            newStatusReason: null,
            phase: createResourceIdentifier('pipeline_phases', phase.id.toString()),
        }) as Referral);

        eventBus.dispatch('resource-manipulated', 'Lead');

        takeOpportunityConfirmationDialog.value?.closeOverlay();
    };

    watch(currentStatusReason, () => {
        if (currentStatusReason.value && currentStatusReason.value.id.value === '') {
            currentStatusReason.value = null;
        }
    });

    watch([currentStatus, currentStatusReason], () => {
        statusHasChanges.value = (
            currentStatus.value.value !== props.lead.status.value
            || (currentStatusReason.value === null && props.lead.reason !== null)
            || (currentStatusReason.value !== null && props.lead.reason === null)
        );
    });

    watch(currentPhaseReason, () => {
        if (currentPhaseReason.value && currentPhaseReason.value.id.value === '') {
            currentPhaseReason.value = null;
        }
    });

    watch([currentPhase, currentPhaseReason], () => {
        stateHasChanges.value = (
            (!currentPhase.value?.id.equals(props.lead.phase?.id))
            || (currentPhaseReason.value === null && props.lead.reason !== null)
            || (currentPhaseReason.value !== null && props.lead.reason === null)
        );
    });

    watch([
        () => props.lead.status,
        () => props.lead.reason,
    ], () => {
        statusHasChanges.value = false;
        stateHasChanges.value = false;
        currentStatus.value = props.lead.status;
        currentStatusReason.value = props.lead.reason;
        moveTiming.cancelled();
    });

    watch([
        () => props.lead.phase,
        () => props.lead.reason,
    ], () => {
        statusHasChanges.value = false;
        stateHasChanges.value = false;
        currentPhase.value = props.lead.phase;
        currentPhaseReason.value = props.lead.reason;
        moveTiming.cancelled();
    });
</script>

<style lang="scss" scoped>
.state-card-border {
    border-top: 2px solid color(gray-200);
}
</style>
